import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import PdfZoomPanWrap from './PdfZoomPanWrap';
import { useAuth } from '../contexts/AuthContext';
import BookFeedback from './BookFeedback';
import '../styles/SinghAmaraBook.css';
import '../styles/HanumanBahukBook.css';

import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

interface Props {
  onNavigate?: (path: string) => void;
}

type PDFDocProxy = Awaited<ReturnType<typeof pdfjsLib.getDocument>['promise']>;
type BookEvent = 'book_open' | 'page_view' | 'book_close';

interface AnalyticsPayload {
  session_id: string;
  event: BookEvent;
  page?: number;
  total_pages?: number;
  duration_s?: number;
}

function fireAnalyticsSync(payload: AnalyticsPayload, token: string): void {
  fetch('/api/books/maataa-pitaa-guru/analytics', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {});
}

export default function MaataaPitaaGuruBook({ onNavigate }: Props) {
  const { user, authLoading } = useAuth();
  const [fetching, setFetching]       = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [pdfDoc, setPdfDoc]           = useState<PDFDocProxy | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages]   = useState(0);
  const [rendering, setRendering]     = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const renderTaskRef = useRef<{ cancel: () => void } | null>(null);
  const renderGenRef  = useRef<number>(0);
  const readerRef = useRef<HTMLElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const sessionIdRef       = useRef<string | null>(null);
  const sessionOpenTimeRef = useRef<number | null>(null);
  const furthestPageRef    = useRef<number>(1);
  const totalPagesRef      = useRef<number>(0);
  const tokenRef           = useRef<string | null>(null);
  const sessionClosedRef   = useRef<boolean>(true);

  // ── Page deep-link state ──────────────────────────────────────────────────
  const [shareCopied, setShareCopied] = useState(false);

  // Read ?page=N from URL once on mount
  const initialPage = useMemo(() => {
    const p = parseInt(new URLSearchParams(window.location.search).get('page') ?? '1', 10);
    return isNaN(p) || p < 1 ? 1 : p;
  }, []);
  const pendingPageRef = useRef<number>(initialPage);

  useEffect(() => { totalPagesRef.current = totalPages; }, [totalPages]);

  const flushSession = useCallback((): void => {
    if (sessionClosedRef.current) return;
    if (!sessionIdRef.current || !tokenRef.current) return;
    sessionClosedRef.current = true;
    const duration_s = sessionOpenTimeRef.current
      ? Math.round((Date.now() - sessionOpenTimeRef.current) / 1000)
      : 0;
    fireAnalyticsSync(
      { session_id: sessionIdRef.current, event: 'book_close',
        page: furthestPageRef.current, total_pages: totalPagesRef.current, duration_s },
      tokenRef.current,
    );
    sessionIdRef.current = null;
    tokenRef.current = null;
  }, []);

  useEffect(() => { return () => { flushSession(); }; }, [flushSession]);

  useEffect(() => {
    const onHidden = (): void => { if (document.visibilityState === 'hidden') flushSession(); };
    document.addEventListener('visibilitychange', onHidden);
    return () => document.removeEventListener('visibilitychange', onHidden);
  }, [flushSession]);

  const renderPage = useCallback(async (doc: PDFDocProxy, pageNum: number) => {
    const gen = ++renderGenRef.current;

    renderTaskRef.current?.cancel();
    renderTaskRef.current = null;

    const canvas = canvasRef.current;
    if (!canvas) return;
    setRendering(true);
    try {
      const page = await doc.getPage(pageNum);
      if (gen !== renderGenRef.current) return;

      const context = canvas.getContext('2d');
      if (!context) return;
      const dpr           = window.devicePixelRatio || 1;
      const viewport0     = page.getViewport({ scale: 1 });
      const isFs          = !!document.fullscreenElement;
      let containerWidth: number;
      if (isFs) {
        const availH     = window.innerHeight - 62;
        const widthByH   = (availH / viewport0.height) * viewport0.width;
        containerWidth   = Math.min(widthByH, window.innerWidth * 0.98);
      } else {
        containerWidth   = Math.min(canvas.parentElement?.clientWidth ?? 800, 860);
      }
      const scale         = (containerWidth / viewport0.width) * dpr;
      const viewport  = page.getViewport({ scale });
      canvas.width        = viewport.width;
      canvas.height       = viewport.height;
      canvas.style.width  = `${viewport.width  / (window.devicePixelRatio || 1)}px`;
      canvas.style.height = `${viewport.height / (window.devicePixelRatio || 1)}px`;
      const task = page.render({ canvas, canvasContext: context, viewport });
      renderTaskRef.current = task;
      await task.promise;
      renderTaskRef.current = null;
    } catch (err: unknown) {
      if ((err as { name?: string })?.name !== 'RenderingCancelledException') {
        console.error('PDF render error', err);
      }
    } finally {
      if (gen === renderGenRef.current) setRendering(false);
    }
  }, []);

  useEffect(() => {
    if (pdfDoc) renderPage(pdfDoc, currentPage);
  }, [pdfDoc, currentPage, renderPage]);

  useEffect(() => {
    if (!pdfDoc || sessionClosedRef.current || !sessionIdRef.current || !tokenRef.current) return;
    const sid   = sessionIdRef.current;
    const token = tokenRef.current;
    const page  = currentPage;
    if (page > furthestPageRef.current) furthestPageRef.current = page;
    const tid = setTimeout(() => {
      fireAnalyticsSync(
        { session_id: sid, event: 'page_view', page, total_pages: totalPagesRef.current },
        token,
      );
    }, 500);
    return () => clearTimeout(tid);
  }, [pdfDoc, currentPage]);

  // Auto-load when URL has ?page=N on first render (deep link support)
  useEffect(() => {
    if (user && !pdfDoc && !fetching && pendingPageRef.current > 1) {
      handleReadBook();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Navigate to the pending page once the PDF is loaded
  useEffect(() => {
    if (pdfDoc && totalPages > 0 && pendingPageRef.current > 1) {
      const p = Math.max(1, Math.min(pendingPageRef.current, totalPages));
      pendingPageRef.current = 0;
      setCurrentPage(p);
    }
  }, [pdfDoc, totalPages]);

  const handleReadBook = useCallback(async () => {
    if (!user) return;
    setError(null);
    setFetching(true);
    try {
      const token = await user.getIdToken();
      const resp = await fetch('/api/books/maataa-pitaa-guru/maataa_pitaa_guru_book.pdf', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.status === 429) {
        const retryAfter = resp.headers.get('Retry-After');
        const minutes = retryAfter ? Math.ceil(Number(retryAfter) / 60) : null;
        throw new Error(
          minutes
            ? `Reading limit reached. Please try again in ${minutes} minute(s).`
            : 'Reading limit reached. Please try again later.',
        );
      }
      if (!resp.ok) throw new Error(`Server returned ${resp.status}`);

      const arrayBuffer = await resp.arrayBuffer();
      const doc   = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const pages = doc.numPages;

      const newSessionId = crypto.randomUUID();
      sessionIdRef.current       = newSessionId;
      tokenRef.current           = token;
      sessionOpenTimeRef.current = Date.now();
      furthestPageRef.current    = 1;
      sessionClosedRef.current   = false;
      totalPagesRef.current      = pages;

      fireAnalyticsSync(
        { session_id: newSessionId, event: 'book_open', page: 1, total_pages: pages },
        token,
      );

      const targetPage = pendingPageRef.current > 1
        ? Math.max(1, Math.min(pendingPageRef.current, pages))
        : 1;
      pendingPageRef.current = 0;
      setTotalPages(pages);
      setCurrentPage(targetPage);
      setPdfDoc(doc);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load book. Please try again.');
    } finally {
      setFetching(false);
    }
  }, [user]);

  const goTo = useCallback((n: number) => {
    if (!pdfDoc) return;
    setCurrentPage(Math.max(1, Math.min(n, totalPages)));
  }, [pdfDoc, totalPages]);

  const handleSharePage = useCallback(async () => {
    const url = window.location.href;
    if (navigator.share) {
      await navigator.share({ title: document.title, url }).catch(() => {});
    } else {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2200);
    }
  }, []);

  const closeReader = useCallback(() => {
    flushSession();
    setPdfDoc(null);
    setCurrentPage(1);
    setTotalPages(0);
  }, [flushSession]);

  const toggleFullscreen = useCallback(async () => {
    if (!readerRef.current) return;
    if (!document.fullscreenElement) {
      await readerRef.current.requestFullscreen().catch(() => {});
    } else {
      await document.exitFullscreen().catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (!pdfDoc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goTo(currentPage - 1);
      if (e.key === 'ArrowRight') goTo(currentPage + 1);
      if (e.key.toLowerCase() === 'f') toggleFullscreen();
      if (e.key === 'Escape' && document.fullscreenElement) document.exitFullscreen().catch(() => {});
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pdfDoc, currentPage, goTo, toggleFullscreen]);

  useEffect(() => {
    if (!pdfDoc) return;
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(currentPage));
    history.replaceState(null, '', url.toString());
  }, [pdfDoc, currentPage]);

  // Fullscreen sync + re-render current page after toggle (layout may change)
  useEffect(() => {
    const onFsChange = (): void => {
      const active = !!document.fullscreenElement;
      setIsFullscreen(active);
      if (pdfDoc) setTimeout(() => renderPage(pdfDoc, currentPage), 150);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, [pdfDoc, currentPage, renderPage]);

  useEffect(() => { return () => { flushSession(); }; }, [flushSession]);

  if (authLoading) {
    return <div className="singh-amara-page hanuman-bahuk-page"><p className="gate-body">Loading…</p></div>;
  }
  if (!user) {
    return (
      <div className="singh-amara-page hanuman-bahuk-page">
        <div className="singh-amara-gate">
          <div className="gate-lion hanuman-bahuk-gate-emblem" aria-hidden="true">🕉️</div>
          <h1 className="gate-title">माता-पिता-और-गुरु</h1>
          <p className="gate-subtitle">शिष्य-संवाद · गुरु-प्राप्ति के उपाय</p>
          <p className="gate-body">
            यह पुस्तक पंजीकृत उपयोगकर्ताओं के लिए उपलब्ध है।
            <br />
            <span className="gate-body-en">This book is available to registered users.</span>
          </p>
          <button className="gate-btn hanuman-bahuk-gate-btn" onClick={() => onNavigate?.('/profile')}>
            साइन इन करें · Sign In
          </button>
          <button className="gate-back-btn" onClick={() => onNavigate?.('/products')}>
            ← Products
          </button>
        </div>
      </div>
    );
  }

  if (!pdfDoc) {
    return (
      <div className="singh-amara-page hanuman-bahuk-page">
        <nav className="singh-amara-nav">
          <button className="back-btn" onClick={() => onNavigate?.('/products')}>← Products</button>
        </nav>
        <header className="singh-amara-header hanuman-bahuk-header">
          <div className="header-emblem" aria-hidden="true">🕉️</div>
          <div className="header-text">
            <h1>माता-पिता-और-गुरु — चेतन · धीर · गुरु-प्राप्ति</h1>
            <p className="header-subtitle">Maataa-Pitaa-Aur-Guru · Student Dialogue on Finding the Guru</p>
            <p className="header-meta">
              श्रीमहाभारत शान्तिपर्व · दो शिष्यों का सरल संस्कृत संवाद · १४ दृश्य · राजपूत-मेवाड़ लघुचित्र
            </p>
          </div>
        </header>

        {error && <div className="singh-amara-error" role="alert">{error}</div>}

        <section className="singh-amara-actions">
          <button
            className="action-btn view-btn hanuman-bahuk-read-btn"
            onClick={handleReadBook}
            disabled={fetching}
            aria-busy={fetching}
          >
            {fetching
              ? <><span className="btn-spinner" aria-hidden="true" /> Loading Book…</>
              : '📖 पुस्तक पढ़ें · Read Book'}
          </button>
        </section>

        <section className="singh-amara-details">
          <div className="detail-card">
            <h2>पुस्तक में</h2>
            <ul>
              <li>🕉️ माता-पिता पहले गुरु — तीन अग्नियाँ, तीन लोक</li>
              <li>📖 चेतन और धीर के बीच सरल संस्कृत संवाद (प्रारम्भिक अभ्यास हेतु)</li>
              <li>🎨 १४ राजपूत लघुचित्र — मेवाड़ शैली</li>
              <li>📐 US Letter लैंडस्केप — चित्र + संस्कृत + हिन्दी</li>
            </ul>
          </div>
          <div className="detail-card">
            <h2>Book Contents</h2>
            <ul>
              <li>🕉️ Mother &amp; Father as first Gurus — the three fires, three worlds</li>
              <li>📖 Simple Sanskrit dialogues between Chetana &amp; Dhīra (beginner level)</li>
              <li>🎨 14 Rajput miniature illustrations — Mewar school</li>
              <li>📐 US Letter landscape — image + Sanskrit + Hindi</li>
            </ul>
          </div>
          <p className="read-only-notice">🔒 Available for online reading only. Source: Mahābhārata Śāntiparva Ch.108 (Gītā Press).</p>
        </section>
      </div>
    );
  }

  return (
    <div className="singh-amara-page hanuman-bahuk-page">
      <nav className="singh-amara-nav">
        <button className="back-btn" onClick={() => { closeReader(); onNavigate?.('/products'); }}>
          ← Products
        </button>
      </nav>

      <section className="pdf-reader" ref={readerRef as React.RefObject<HTMLElement>}>
        <div className="pdf-toolbar" onContextMenu={(e) => e.preventDefault()}>
          <button
            className="pdf-nav-btn"
            onClick={() => goTo(currentPage - 1)}
            disabled={currentPage <= 1 || rendering}
            aria-label="Previous page"
          >‹</button>

          <span className="pdf-page-info">
            {currentPage} / {totalPages}
          </span>

          <button
            className="pdf-nav-btn"
            onClick={() => goTo(currentPage + 1)}
            disabled={currentPage >= totalPages || rendering}
            aria-label="Next page"
          >›</button>

          <button
            className={`pdf-share-btn${shareCopied ? ' pdf-share-copied' : ''}`}
            onClick={handleSharePage}
            title={shareCopied ? 'Copied!' : 'Share page link'}
            aria-label={shareCopied ? 'Copied!' : 'Share page link'}
          >
            {shareCopied ? '✓' : '⎘'}
          </button>

          <button className="pdf-fullscreen-btn" onClick={toggleFullscreen} title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
            {isFullscreen ? '⊠' : '⛶'}
          </button>

          <button
            className="pdf-close-btn"
            onClick={closeReader}
            aria-label="Close reader"
          >✕</button>
        </div>

        <PdfZoomPanWrap onContextMenu={(e) => e.preventDefault()} resetKey={currentPage}>
          {rendering && (
            <div className="pdf-rendering-overlay" aria-live="polite">
              <span className="loading-spinner" aria-hidden="true" />
            </div>
          )}
          <canvas ref={canvasRef} className="pdf-canvas" />
        </PdfZoomPanWrap>
      </section>
      {pdfDoc && (
        <BookFeedback
          bookId="maataa-pitaa-guru"
          bookTitle="माता-पिता-और-गुरु — चेतन · धीर · गुरु-प्राप्ति"
          page={currentPage}
        />
      )}
    </div>
  );
}
